"use client";
import { CheckoutHeader } from "@/components/checkout/checkoutHeader";
import { CustomerProfileHeader } from "@/components/customerProfile/customerProfileHeader";
import { formatTime } from "@/components/merchantFooter/operatingHours";
import MerchantLinkButton from "@/components/merchantLinkButton/merchantLinkButton";
import { NavigationBar } from "@/components/navigationBar/navigationBar";
import { NotAcceptingOrdersBanner } from "@/components/notAcceptingOrdersBanner/notAcceptingOrdersBanner";
import { Surcharge } from "@/components/surcharge/surcharge";
import { VerifyEmailHeader } from "@/components/verifyEmail/verifyEmailHeader";
import { Address } from "@/dto/address";
import { DayPart, DayPartMenuCategories } from "@/dto/dayPartMenu";
import { Service } from "@/dto/service";
import { OrderType } from "@/types";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { daysOfWeek } from "@/utils/dateTimeUtils";
import { giftCardRoute } from "@/utils/giftCardUtils";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { canMerchantAcceptOrders } from "@/utils/scheduleOrderUtils";
import {
  getFromSessionStorage,
  removeFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import ClockIcon from "./clockIcon.svg";
import { CookiesBanner } from "./cookiesBanner";
import DownArrow from "./downArrow.svg";
import { HeaderMerchantInfoContainer } from "./headerMerchantInfoContainer";
import { BusinessHoursModal } from "./hoursModal";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";

export interface HeaderProps {
  merchantLogo?: string;
  merchantName?: string;
  merchant: any;
  menu: any;
  merchantPreferences: any;
  deleteSession: any;
  customerDetails: any;
  merchantPaymentConfig?: any;
  retailCategories?: any;
  serviceCategories?: any;
  redirectToGiftCard: boolean;
  defaultAddress: Address | null;
  isLoading: boolean;
  pickupTimePromise?: Promise<any>;
  dayPartMenus?: DayPart[];
  dayPartMenuCategories?: DayPartMenuCategories[];
}

interface TimeSlot {
  start: string;
  end: string;
}

interface TimeSlotProps extends TimeSlot {
  abbreviation: string;
}

interface MainTimeSlotProps {
  todayHours: TimeSlot[];
  abbreviation: string;
}

const commonClasses = ["text-[#373737]", "items-right", "cursor-pointer"].join(
  " "
);

const TimeSlot = ({ start, end, abbreviation }: TimeSlotProps) => {
  const t = useTranslations("Header");
  return (
    <span className="whitespace-nowrap">
      {t("timeRange", {
        start: formatTime(start),
        end: formatTime(end),
        abbreviation,
      })}
    </span>
  );
};

const MainTimeSlot = ({ todayHours, abbreviation }: MainTimeSlotProps) => {
  const t = useTranslations("Header");
  return (
    <div className={`${commonClasses} flex justify-center sm:justify-end`}>
      <Image
        src={ClockIcon.src}
        height={14}
        width={14}
        style={{ marginTop: "5px" }}
        alt="clock icon"
      />
      <span className="font-medium px-1">{t("open")}</span>
      <TimeSlot
        start={todayHours[0].start}
        end={todayHours[0].end}
        abbreviation={abbreviation}
      />

      <Image
        src={DownArrow.src}
        height={20}
        width={20}
        alt={t("openBusinessHoursAlt")}
      />
    </div>
  );
};

const AdditionalTimeSlots = ({
  todayHours,
  abbreviation,
}: MainTimeSlotProps) => {
  return (
    <div className={`${commonClasses} pr-5`}>
      {todayHours.slice(1, 3).map((timeSlot: TimeSlot, index: number) => (
        <div key={index} className="whitespace-nowrap">
          <TimeSlot
            start={timeSlot.start}
            end={timeSlot.end}
            abbreviation={abbreviation}
          />
        </div>
      ))}
    </div>
  );
};

const MoreIndicator = () => {
  return (
    <div className={`${commonClasses} pr-5`}>
      <span className="ml-1 text-blue-600 hover:text-blue-800">...</span>
    </div>
  );
};

export const Header = ({
  merchantLogo,
  merchantName,
  merchant,
  menu,
  merchantPreferences,
  deleteSession,
  customerDetails,
  merchantPaymentConfig,
  retailCategories,
  serviceCategories,
  pickupTimePromise,
  redirectToGiftCard,
  defaultAddress,
  isLoading,
  dayPartMenus,
  dayPartMenuCategories,
}: HeaderProps) => {
  const t = useTranslations("Header");
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const orderType = searchParams?.get("order_type");
  const menuName = searchParams?.get("menu");
  const isDineIn = orderType?.toLowerCase() === OrderType.DINE_IN.toLowerCase();
  const merchantColor =
    merchant?.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const isSimpleStorefront = merchant?.merchantMeta?.selectedSetup === "Simple";
  const isPreviewSite =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("preview-");
  const isStandaloneGiftCard =
    merchant?.isStandaloneGiftCardWebsite && !isPreviewSite;
  const [hoursModalIsOpen, setHoursModalIsOpen] = useState<boolean>(false);
  const currentDay = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const tableData = getFromLocalStorage(LocalStorageKey.TABLE_DATA, undefined);
  const scanToOrderInfo = getFromLocalStorage(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    {
      tableId: "",
      employeeId: "",
      orderType: OrderType.PICKUP as OrderType,
    }
  );
  const isScanToOrder = tableData && scanToOrderInfo;
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);

  const getCurrentDayPart = (
    dayPartMenus: DayPart[] = [],
    menuName: string | null = null
  ) => {
    if (dayPartMenus && dayPartMenus.length > 0) {
      if (menuName && !isDineIn) {
        const preSelectedMenu = dayPartMenus.find(
          (menu) => menu.formattedName === menuName
        );

        if (preSelectedMenu) return preSelectedMenu;
      }

      const sessionDayPartMenu = getFromSessionStorage<{
        id?: string;
        menuId?: string;
      }>(SessionStorageKey.DAYPART_MENU, {});
      if (sessionDayPartMenu?.id && !isDineIn) {
        const sessionMenu = dayPartMenus.find(
          (menu) => menu.id === sessionDayPartMenu.id
        );
        if (sessionMenu) return sessionMenu;
      }

      const activeMenu = dayPartMenus.find((menu) => menu.activeNow);
      if (activeMenu) return activeMenu;

      const upcomingMenu = dayPartMenus.find((menu) => menu.upcoming);
      if (upcomingMenu) return upcomingMenu;

      return dayPartMenus[0];
    }

    return null;
  };

  const [selectedMenu, setSelectedMenu] = useState<DayPart | null>(null);

  useEffect(() => {
    const currentDayPart = getCurrentDayPart(dayPartMenus, menuName);

    if (currentDayPart)
      saveToSessionStorage(SessionStorageKey.DAYPART_MENU, {
        id: currentDayPart.id,
        menuId: currentDayPart.menuId,
        name: currentDayPart.formattedName,
      });

    setSelectedMenu(currentDayPart);
  }, [menuName, dayPartMenus]);

  const service = merchant?.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const headerBusinessHours = merchant?.isRestaurant
    ? service?.hours
    : merchant?.businessHours?.hours;

  useEffect(() => {
    const protectedPaths = [
      "/guest-checkout",
      "/checkout",
      "/confirmation",
      "/customer-profile",
      "/my-orders",
      "/my-appointments",
    ];

    if (protectedPaths.includes(pathName!)) {
      return;
    }
    const handleBeforeUnload = () => {
      removeFromSessionStorage(SessionStorageKey.SCHEDULED_TIME);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pathName]);

  const handleHoursModal = () => {
    setHoursModalIsOpen(!hoursModalIsOpen);
  };
  const abbreviation = getTimezoneAbbreviation(
    merchant?.timezone,
    merchant?.locale
  );

  if (pathName == "/customer-profile") {
    return <CustomerProfileHeader />;
  }

  if (pathName == "/verify-email") {
    return <VerifyEmailHeader />;
  }

  if (pathName == "/checkout" || pathName == "/guest-checkout") {
    return (
      <CheckoutHeader
        redirectToGiftCard={redirectToGiftCard}
        isRunningTabsEnabled={merchant.isRunningTabsEnabled}
      />
    );
  }

  if (pathName == "/confirmation" && isScanToOrder) {
    return null;
  }
  const todayHours =
    headerBusinessHours !== undefined &&
    headerBusinessHours[currentDay] !== undefined
      ? headerBusinessHours[currentDay]
      : [];
  const hasHours = todayHours.length > 0;

  const isAcceptingOrders = canMerchantAcceptOrders(
    merchant,
    isDineIn,
    selectedMenu
  );

  const isNotAcceptingOrder = !isAcceptingOrders || !merchant.isCheckoutEnabled;

  return (
    <nav
      className={[
        "flex",
        "flex-col",
        "items-center",
        "relative",
        "top-0",
        "z-5",
        "bg-[#FBFBFB]",
      ].join(" ")}
    >
      <CookiesBanner />
      <div
        className={[
          ...[
            "flex",
            `${isSimpleStorefront && "flex-col"}`,
            "sm:flex-row",
            "px-10",
            "pt-6",
            "w-full",
            "justify-between",
          ],
          // add padding on the bottom of the header when navbar is removed
          ...(isStandaloneGiftCard ? ["pb-5"] : []),
        ].join(" ")}
      >
        <HeaderMerchantInfoContainer
          merchantLogo={merchantLogo}
          merchantName={merchantName}
          isSimpleStorefront={isSimpleStorefront}
          isStandaloneGiftCard={isStandaloneGiftCard}
          merchantAddress={merchant?.address}
          merchantPhone={merchant?.phone}
        />
        {isSimpleStorefront && pathName !== "/confirmation" && (
          <div
            className={`${
              !merchant.isRestaurant && "hidden"
            } sm:flex flex-row justify-center items-right`}
          >
            <div onClick={handleHoursModal} className="text-center">
              <div className={`${commonClasses} pb-4`}>
                {hasHours ? (
                  <>
                    <MainTimeSlot
                      todayHours={todayHours}
                      abbreviation={abbreviation}
                    />

                    {todayHours.length > 1 && (
                      <AdditionalTimeSlots
                        todayHours={todayHours}
                        abbreviation={abbreviation}
                      />
                    )}
                    {todayHours.length > 3 && <MoreIndicator />}
                  </>
                ) : (
                  <span className="flex justify-end font-medium px-1 pr-5">
                    {t("closed")}
                  </span>
                )}
                {merchantPaymentConfig &&
                  merchantPaymentConfig.surcharging?.rate > 0 && (
                    <Surcharge
                      isTimeSlot={true}
                      surchargeRate={merchantPaymentConfig.surcharging?.rate}
                    />
                  )}
              </div>
            </div>
            <BusinessHoursModal
              isOpen={hoursModalIsOpen}
              onRequestClose={handleHoursModal}
              daysOfWeek={daysOfWeek}
              businessHours={headerBusinessHours}
              timezone={abbreviation}
              isRestaurantMerchant={merchant?.isRestaurant}
            />
          </div>
        )}
        {pathName !== "/confirmation" &&
          !isSimpleStorefront &&
          !isStandaloneGiftCard &&
          !pathName?.startsWith("/services") && (
            <div className={["flex flex-col gap-6 items-center"].join(" ")}>
              <HeaderLinkButton
                merchant={merchant}
                merchantColor={merchantColor}
                selectedDayPart={selectedMenu}
              />
              {/* need to check where to render surcharge for not simple strorefront? */}
            </div>
          )}
        {pathName === "/confirmation" && (
          <p className="hidden sm:block font-normal text-[15px]">
            <MerchantLinkButton
              merchantColor={merchantColor}
              uri="/"
              text={t("keepBrowsing")}
            />
          </p>
        )}
      </div>
      {pathName !== "/confirmation" && !isStandaloneGiftCard && (
        <div className={["w-full"].join(" ")}>
          <NavigationBar
            merchant={merchant}
            menu={menu}
            customerPerks={customerDetails?.earnedPerks}
            merchantPreferences={merchantPreferences}
            deleteSession={deleteSession}
            retailCategories={retailCategories}
            serviceCategories={serviceCategories}
            pickupTimePromise={pickupTimePromise}
            defaultAddress={defaultAddress}
            isLoading={isLoading}
            setIsCategoryMenuOpen={setIsCategoryMenuOpen}
            isCategoryMenuOpen={isCategoryMenuOpen}
            dayPartMenus={dayPartMenus}
            dayPartMenuCategories={dayPartMenuCategories}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
            isNotAcceptingOrder={isNotAcceptingOrder}
          />
        </div>
      )}
      <NotAcceptingOrdersBanner
        merchant={merchant}
        isNotAcceptingOrder={isNotAcceptingOrder}
        selectedDayPart={selectedMenu}
      />
    </nav>
  );
};

const HeaderLinkButton = ({
  merchant,
  merchantColor,
  selectedDayPart,
}: any) => {
  const t = useTranslations("Header");
  const pathName = usePathname();
  const isNotRestaurantOfferShown = !pathName
    ?.toLocaleLowerCase()
    .includes("menu");
  const isNotEssentialMerchantOfferShown = !pathName
    ?.toLocaleLowerCase()
    .includes("categories");
  const isOnGiftCardPage = pathName === giftCardRoute;
  const scanToOrderInfo = getFromLocalStorage(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    null
  );
  let queryParams = "";
  if (scanToOrderInfo) {
    queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  let menuUri = merchant?.isEssentialMerchant ? "/categories/all" : "/menu/all";

  if (!isEmpty(selectedDayPart))
    queryParams += queryParams
      ? `&menu=${selectedDayPart.formattedName}`
      : `?menu=${selectedDayPart.formattedName}`;

  if (queryParams) {
    menuUri += queryParams;
  }

  return (
    <div className={isOnGiftCardPage ? "hidden md:block" : ""}>
      {merchant?.isService && (
        <MerchantLinkButton
          merchantColor={merchantColor}
          uri="/services/all"
          text={t("bookNow")}
        />
      )}
      {merchant?.isRetail && (
        <MerchantLinkButton
          merchantColor={merchantColor}
          uri="/all"
          text={t("shopAll")}
        />
      )}
      {(merchant?.isRestaurant || merchant?.isEssentialMerchant) &&
        (isNotRestaurantOfferShown || !merchant?.isCheckoutEnabled) &&
        isNotEssentialMerchantOfferShown && (
          <MerchantLinkButton
            merchantColor={merchantColor}
            uri={menuUri}
            text={t(merchant?.isCheckoutEnabled ? "orderOnline" : "viewItems")}
          />
        )}
    </div>
  );
};
